import { customFetch } from "../../utils/Auth";
import { environment } from "../../config/environment";

export const SCOPE_TYPES = {
    ORG: "ORG",
    SPECIALTY: "SPECIALTY",
    DOCTOR: "DOCTOR",
};

export const SCOPE_LABELS = {
    ORG: "Org",
    SPECIALTY: "Specialty",
    DOCTOR: "Doctor",
};

const readError = async (response, fallback) => {
    try {
        const body = await response.json();
        return body?.error || body?.details || fallback;
    } catch {
        return fallback;
    }
};

const getJson = async (path, fallbackError) => {
    const response = await customFetch(`${environment.apiUrl}${path}`, {
        method: "GET",
    });
    if (!response.ok) {
        throw new Error(await readError(response, fallbackError));
    }
    return response.json();
};

export const fetchDictionary = async (scopeType, scopeId) => {
    const query = new URLSearchParams({ scope_type: scopeType, scope_id: scopeId });
    const body = await getJson(
        `/custom-dictionary/dictionary?${query}`,
        "Failed to load the dictionary for this scope.",
    );
    return body?.dictionary ?? null;
};

export const fetchDictionariesByScopeType = async (scopeType) => {
    const query = new URLSearchParams({ scope_type: scopeType });
    const body = await getJson(
        `/custom-dictionary/dictionaries?${query}`,
        "Failed to load dictionaries.",
    );
    const byScopeId = new Map();
    for (const dictionary of body?.dictionaries || []) {
        if (dictionary?.scope_id) {
            byScopeId.set(String(dictionary.scope_id), dictionary);
        }
    }
    return byScopeId;
};

export const saveDictionary = async (scopeType, scopeId, keywords) => {
    const response = await customFetch(
        `${environment.apiUrl}/custom-dictionary/dictionary`,
        {
            method: "PUT",
            body: JSON.stringify({ scope_type: scopeType, scope_id: scopeId, keywords }),
        },
    );
    if (!response.ok) {
        throw new Error(await readError(response, "Failed to save this dictionary."));
    }
    return response.json();
};

export const deleteDictionary = async (scopeType, scopeId) => {
    const query = new URLSearchParams({ scope_type: scopeType, scope_id: scopeId });
    const response = await customFetch(
        `${environment.apiUrl}/custom-dictionary/dictionary?${query}`,
        { method: "DELETE" },
    );
    if (!response.ok) {
        throw new Error(await readError(response, "Failed to delete this dictionary."));
    }
    return response.json();
};

export const fetchOrgs = () =>
    getJson("/custom-dictionary/orgs", "Failed to load orgs.");

export const fetchAllDoctors = () =>
    getJson("/custom-dictionary/doctors", "Failed to load doctors.");

export const fetchSpecialtiesByOrg = (org) =>
    getJson(
        `/doctors/specialties-by-org/${encodeURIComponent(org)}`,
        `Failed to load specialties for ${org}.`,
    );

export const fetchDoctorsBySpecialty = (specialty, org) => {
    const query = new URLSearchParams();
    if (org) query.set("org", org);
    const suffix = query.toString() ? `?${query}` : "";
    return getJson(
        `/doctors/by-specialty/${encodeURIComponent(specialty)}${suffix}`,
        `Failed to load doctors for ${specialty}.`,
    );
};

export const fetchDoctor = (userId) =>
    getJson(
        `/doctors/doctor-by-user-id/${encodeURIComponent(userId)}`,
        "Failed to load this doctor.",
    );

export const fetchDoctorMappings = async (userId) => {
    const body = await getJson(
        `/custom-dictionary/doctor-mappings/${encodeURIComponent(userId)}`,
        "Failed to load this doctor's org mappings.",
    );
    return Array.isArray(body?.mappings) ? body.mappings : [];
};
